import {
	dateDiff,
	DateDiffPrecision,
	dateEquals,
	dateIncrement,
	getFirstDayOfMonth,
	getFirstDayOfWeek,
	getLastDayOfMonth,
	getLastDayOfWeek
} from './date-helpers';
import { ICalendarEvent, ICalendarPrice, IEventBlock } from './types';

import { FIRST_DAY_OF_WEEK } from './config';

export const gridLocation = (start: Date, end: Date) => {
	// week of month calculation
	const firstDayOfMonth = getFirstDayOfMonth(start);
	const firstWeekdayForDayOfMonth = getFirstDayOfWeek(firstDayOfMonth, FIRST_DAY_OF_WEEK);
	const monthStartOffset = dateDiff(firstWeekdayForDayOfMonth, firstDayOfMonth);
	const weekOfMonth = Math.ceil((start.getDate() + monthStartOffset) / 7) - 1;

	// day of week calculation
	const firstDay = getFirstDayOfWeek(start, FIRST_DAY_OF_WEEK);
	const dayofWeek = dateDiff(firstDay, start);

	const nights = dateDiff(start, end);
	return {
		day: dayofWeek,
		week: weekOfMonth,
		nights,
	};
};

export const generateEventBlock = (start: Date, end: Date, event: ICalendarEvent): IEventBlock => {
	// take a full calendar event and generate a renderable event block.
	// the block may show on the calendar with a different start and end date to the original
	// depending on whether the original spans multiple weeks/months.
	// we keep a reference to the original calendar event under the "event" key
	// so we can render text based on the event data, and link off to relevant pages
	const { day, week, nights } = gridLocation(start, end);
	const eventContainsStart = dateEquals(start, event.start);
	const eventContainsEnd = dateEquals(end, event.end);

	return {
		event,
		start,
		end,
		day,
		week,
		nights,
		blockContainsStart: eventContainsStart,
		blockContainsEnd: eventContainsEnd,
		contiguousEvent: false,
	};
};

export const expandEvent = (expanded: IEventBlock[], event: ICalendarEvent): IEventBlock[] => {
	// for each event, take the start and end date and work out if it spans multiple grid rows
	const firstDayOfWeek = getFirstDayOfWeek(event.start);
	const numberOfWeeks = dateDiff(firstDayOfWeek, event.end, DateDiffPrecision.WEEKS);
	if (numberOfWeeks > 0) {
		// split out the current event as many times as required.
		// eventContainsStart and eventContainsEnd are used to style the events programatically.

		// get the last day of the current week. use this as the first event block end date
		const lastDayOfWeek = getLastDayOfWeek(event.start, FIRST_DAY_OF_WEEK);
		const first = generateEventBlock(event.start, lastDayOfWeek, event);

		// get the first day of the last week of the event. use this as the last event block start date.
		const firstDayOfLastWeek = getFirstDayOfWeek(event.end);
		const last = generateEventBlock(firstDayOfLastWeek, event.end, event);

		// use the first day of the second week of the event as a starting point
		// building up a list of fully blocked weeks inbetween the starting and ending week
		const firstDayNextWeek = new Date(lastDayOfWeek);
		firstDayNextWeek.setDate(firstDayNextWeek.getDate() + 1);
		// fill in the remaining event dates
		const weeks: ICalendarEvent[] = new Array(numberOfWeeks - 1).fill(event);
		const inbetweenEvents: IEventBlock[] = weeks.map((evt, i) => {
			const start = dateIncrement(firstDayNextWeek, 7 * i);
			const end = dateIncrement(firstDayNextWeek, 6 + (7 * i));
			return generateEventBlock(start, end, evt);
		});

		return [...expanded, first, ...inbetweenEvents, last];
	} else {
		return [...expanded, generateEventBlock(event.start, event.end, event)];
	}
};

export const trimAndConvertToBlock = (month: Date) => {
	return (event: IEventBlock): IEventBlock => {
		let { start, end } = event;

		// if an event's start or end dates fall across months, we need to
		// trim those dates to prevent the event block from being visible outwith the current rendered month
		if (start < getFirstDayOfMonth(month)) {
			start = getFirstDayOfMonth(month);
		}
		if (end > getLastDayOfMonth(month)) {
			end = getLastDayOfMonth(month);
		}

		return generateEventBlock(start, end, event.event);
	};
};

export const monthlyEventFilter = (month: Date) => {
	const monthEnd = getLastDayOfMonth(month);
	const monthStart = getFirstDayOfMonth(month);
	return (event: { start: Date; end: Date; }) => event.start <= monthEnd && event.end >= monthStart;
};

export const monthlyPriceFilter = (month: Date) => {
	const monthEnd = getLastDayOfMonth(month);
	const monthStart = getFirstDayOfMonth(month);
	return (nightlyPrice: ICalendarPrice) => nightlyPrice.date <= monthEnd && nightlyPrice.date >= monthStart;
};
